import React from 'react';
import {
  Datagrid,
  DateField,
  FunctionField,
  ReferenceField,
  ReferenceManyField,
  Show,
  TextField,
  useRecordContext
} from 'react-admin';
import {
  Avatar,
  Box,
  Breadcrumbs,
  Chip,
  Container,
  Divider,
  Grid,
  Link as MuiLink,
  Stack,
  Typography
} from '@mui/material';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { OPPORTUNITY_STATUS_LABELS } from '../types/OpportunityStatusChoices.ts';
import { NoteListSection } from '../components/notes/NoteListSection.tsx';
import { DocumentListSection } from '../components/documents/DocumentListSection.tsx';
import { ConditionalFollowUpDateField } from '../components/ConditionalFollowUpDateField.tsx';
import { OpportunityProfileReadOnly } from './OpportunityProfileReadOnly.tsx';
import { RenewalsSection } from '../renewals/RenewalsSection.tsx';
import {
  DetailRow,
  EmptyState,
  RecordEditButton,
  RecordHeader,
  RecordPage,
  RecordSection,
  RecordTabs
} from '../components/record/RecordPage.tsx';

type OpportunityRecord = {
  id?: number;
  dealName?: string | null;
  status?: string;
  notes?: string | null;
  followUpDate?: string | null;
  dateOpened?: string | null;
  dateClosed?: string | null;
  dateWon?: string | null;
  contactId?: number;
  referredByContactId?: number | null;
  referredByGroupId?: number | null;
  opportunityStageId?: number | null;
};

const statusColor: Record<string, 'success' | 'error' | 'warning' | 'info'> = {
  OPEN: 'info',
  WON: 'success',
  LOST: 'error',
  LOST_BUT_TRACK: 'warning'
};

const OpportunityOverview = ({ record }: { record: OpportunityRecord }) => (
  <Grid container spacing={2}>
    <Grid size={{ xs: 12, md: 7 }}>
      <RecordSection
        title="Summary"
        action={<RecordEditButton label="Edit" variant="text" />}
        sx={{ height: '100%' }}
      >
        <Stack spacing={0.25} divider={<Divider />}>
          <DetailRow icon={<PersonOutlineIcon fontSize="small" />} label="Contact">
            <ReferenceField source="contactId" reference="contacts" link="show">
              <FunctionField
                render={(contact: { firstName?: string; lastName?: string }) =>
                  `${contact?.firstName ?? ''} ${contact?.lastName ?? ''}`.trim() || 'Not set'
                }
              />
            </ReferenceField>
          </DetailRow>
          <DetailRow icon={<BusinessOutlinedIcon fontSize="small" />} label="Company">
            <ReferenceField source="contactId" reference="contacts" link={false}>
              <FunctionField
                render={(contact: { company?: { id?: number; name?: string | null } | null }) => {
                  const companyId = contact?.company?.id;
                  const companyName = contact?.company?.name?.trim();
                  if (!companyName) {
                    return (
                      <Typography variant="body2" color="text.secondary">
                        Not set
                      </Typography>
                    );
                  }
                  return companyId ? (
                    <MuiLink component={RouterLink} to={`/companies/${companyId}/show`}>
                      {companyName}
                    </MuiLink>
                  ) : (
                    companyName
                  );
                }}
              />
            </ReferenceField>
          </DetailRow>
          <DetailRow label="Programs">
            <TextField source="opportunityServiceNames" emptyText="None" />
          </DetailRow>
        </Stack>
      </RecordSection>
    </Grid>

    <Grid size={{ xs: 12, md: 5 }}>
      <RecordSection
        title="Timeline"
        action={<RecordEditButton label="Edit" variant="text" />}
        sx={{ height: '100%' }}
      >
        <Stack spacing={0.25} divider={<Divider />}>
          <DetailRow icon={<CalendarTodayOutlinedIcon fontSize="small" />} label="Opened">
            <DateField source="dateOpened" emptyText="Not set" />
          </DetailRow>
          <DetailRow label="Closed">
            <DateField source="dateClosed" emptyText="Not set" />
          </DetailRow>
          <DetailRow label="Won">
            <DateField source="dateWon" emptyText="Not set" />
          </DetailRow>
          <DetailRow label="Follow up">
            <DateField source="followUpDate" emptyText="Not set" />
          </DetailRow>
        </Stack>
      </RecordSection>
    </Grid>

    <Grid size={{ xs: 12, md: 7 }}>
      <RecordSection
        title="General notes"
        action={<RecordEditButton label="Edit" variant="text" />}
        sx={{ height: '100%' }}
      >
        {record.notes?.trim() ? (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {record.notes.trim()}
          </Typography>
        ) : (
          <EmptyState>No general notes on file.</EmptyState>
        )}
      </RecordSection>
    </Grid>

    <Grid size={{ xs: 12, md: 5 }}>
      <RecordSection
        title="Referral source"
        action={<RecordEditButton label="Edit" variant="text" />}
        sx={{ height: '100%' }}
      >
        <Stack spacing={0.25} divider={<Divider />}>
          <DetailRow icon={<PersonOutlineIcon fontSize="small" />} label="Referred by contact">
            {record.referredByContactId ? (
              <ReferenceField source="referredByContactId" reference="contacts" link="show">
                <FunctionField
                  render={(contact: { firstName?: string; lastName?: string }) =>
                    `${contact?.firstName ?? ''} ${contact?.lastName ?? ''}`.trim() || 'Not set'
                  }
                />
              </ReferenceField>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Not set
              </Typography>
            )}
          </DetailRow>
          <DetailRow icon={<GroupsOutlinedIcon fontSize="small" />} label="Referred by group">
            {record.referredByGroupId ? (
              <ReferenceField source="referredByGroupId" reference="groups" link="show">
                <TextField source="name" />
              </ReferenceField>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Not set
              </Typography>
            )}
          </DetailRow>
        </Stack>
      </RecordSection>
    </Grid>

    {record.id && record.contactId ? (
      <Grid size={{ xs: 12 }}>
        <RenewalsSection
          filter={{ opportunityId: record.id }}
          addTo={{ contactId: record.contactId, opportunityId: record.id }}
          description="The renewals this deal is working. They stay with the contact if the deal goes."
        />
      </Grid>
    ) : null}
  </Grid>
);

const OpportunityActivity = () => (
  <RecordSection
    title="Activity"
    description="A chronological view of opportunity outreach and follow-up."
    action={<RecordEditButton label="Manage" variant="outlined" returnTab="activity" />}
  >
    <ReferenceManyField reference="opportunity-activities" target="opportunityId">
      <Datagrid
        bulkActionButtons={false}
        rowClick={false}
        empty={<EmptyState>No activity yet.</EmptyState>}
      >
        <TextField source="type" />
        <TextField source="note" />
        <DateField
          source="dateSent"
          label="Activity date"
          locales="en-US"
          options={{ timeZone: 'UTC' }}
        />
        <ConditionalFollowUpDateField
          source="followUpDate"
          label="Follow up"
          locales="en-US"
          options={{ timeZone: 'UTC' }}
        />
      </Datagrid>
    </ReferenceManyField>
  </RecordSection>
);

const OpportunityShowContent = () => {
  const record = useRecordContext<OpportunityRecord>();
  if (!record) return null;

  const statusKey = record.status ?? '';
  const statusLabel = OPPORTUNITY_STATUS_LABELS[statusKey] ?? statusKey;
  return (
    <RecordPage>
      <RecordHeader
        avatar={
          <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
            <WorkOutlineIcon />
          </Avatar>
        }
        title={record.dealName?.trim() || 'Opportunity'}
        badges={
          <>
            {statusLabel ? (
              <Chip label={statusLabel} color={statusColor[statusKey] ?? 'default'} size="small" />
            ) : null}
            {record.opportunityStageId ? (
              <ReferenceField
                source="opportunityStageId"
                reference="opportunity-stages"
                link={false}
              >
                <FunctionField
                  render={(stage: { name?: string }) =>
                    stage?.name ? <Chip label={stage.name} size="small" variant="outlined" /> : null
                  }
                />
              </ReferenceField>
            ) : null}
          </>
        }
        actions={<RecordEditButton />}
      />
      <RecordTabs
        tabs={[
          {
            value: 'overview',
            label: 'Overview',
            content: <OpportunityOverview record={record} />
          },
          { value: 'activity', label: 'Activity', content: <OpportunityActivity /> },
          {
            value: 'profile',
            label: 'Opportunity profile',
            content: (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                  <RecordEditButton
                    editPath="profile"
                    label="Edit profile"
                    variant="outlined"
                    returnTab="profile"
                  />
                </Box>
                <OpportunityProfileReadOnly />
              </>
            )
          },
          {
            value: 'notes',
            label: 'Notes',
            content: (
              <RecordSection
                title="Notes"
                action={
                  <RecordEditButton
                    editPath="notes"
                    label="Manage"
                    variant="outlined"
                    returnTab="notes"
                  />
                }
              >
                <NoteListSection entityType="OPPORTUNITY" readOnly hideTitle />
              </RecordSection>
            )
          },
          {
            value: 'documents',
            label: 'Documents',
            content: (
              <RecordSection
                title="Documents"
                action={
                  <RecordEditButton
                    editPath="documents"
                    label="Manage"
                    variant="outlined"
                    returnTab="documents"
                  />
                }
              >
                <DocumentListSection entityType="OPPORTUNITY" readOnly hideTitle />
              </RecordSection>
            )
          }
        ]}
      />
    </RecordPage>
  );
};

const OpportunityShow: React.FC = () => (
  <Container maxWidth="lg">
    <Breadcrumbs sx={{ mt: 2, mb: 1 }}>
      <MuiLink component={RouterLink} underline="hover" color="inherit" to="/">
        Dashboard
      </MuiLink>
      <MuiLink component={RouterLink} underline="hover" color="inherit" to="/opportunities">
        Opportunities
      </MuiLink>
      <Typography color="text.primary">View</Typography>
    </Breadcrumbs>
    <Show actions={false} component="div" sx={{ '& .RaShow-noActions': { mt: 0 } }}>
      <OpportunityShowContent />
    </Show>
  </Container>
);

export default OpportunityShow;
